﻿// ============================================================================
// src/lib/pig.ts  —  pig breeding domain constants + helpers
// ----------------------------------------------------------------------------
// Lifted out of main.jsx as prep for Round 6 pig view extractions (pigsHome,
// breeding, pigbatches, farrowing, sows). Pure module-scope; no React, no
// App closure state.
// ============================================================================
import {toISO, addDays, todayISO} from './dateUtils.js';
// Planned-trip rows are owned by src/lib/pigForecast.ts — that module is the
// one that reads and mutates them (addPlannedTrip / movePigsBetweenTrips /
// deletePlannedTripWithReconciliation). This module never touches their
// contents; it only records that the feeder group carries them, so it defers to
// the owner's type instead of describing the row a second time. `import type`
// is erased at compile time, so naming it here adds no runtime dependency.
import type {StoredPlannedTrip} from './pigForecast.js';

// ── Types ─────────────────────────────────────────────────────────────────
// The pig planner's rows live in an app_store jsonb blob, not in typed tables,
// so numeric fields admit `string` as well as `number` (every read below already
// goes through parseInt/parseFloat) and each shape names ONLY the fields these
// helpers touch. Everything else rides along in the index signature as
// `unknown` rather than being invented into a schema nothing can verify.
//
// These are also the types the ppp-feeders-v1 CONSUMERS share — src/lib/
// feedPlanner.ts and src/pig/usePigPlannedTrips.ts read the same objects — so a
// field named here may be one this module never reads itself. That is the point:
// the blob has one owner, and a consumer that had to re-declare the shape would
// be describing the same rows twice.

// Where a breeder pig came from, as stamped on the breeders[] audit row.
export type PigTransferSource = {
  batchName?: string | null;
  subBatchName?: string | null;
  feedAllocationLbs?: number | string | null;
};

export type PigBreederRow = {
  sex?: string | null;
  // Read by feedPlanner's sow/boar head-count, not by this module.
  archived?: boolean | null;
  transferredFromBatch?: PigTransferSource | null;
  [field: string]: unknown;
};

// How many of a trip's pigs came from one sub-batch.
export type PigSubAttribution = {subId?: string | null; count?: number | string | null};

// A breeding pig (sow / gilt / boar) that rode along on a processing trip.
// Written ONLY by the pig_attach_breeder_to_trip RPC (migration 210), which is
// why `breederId` is the identity and `tag`/`sex` are display snapshots kept
// for a record that outlives the breeder row.
export type PigTripAttachedBreeder = {
  breederId?: string | null;
  tag?: string | null;
  sex?: string | null;
  attachedAt?: string | null;
  attachedBy?: string | null;
  [field: string]: unknown;
};

export type PigProcessingTrip = {
  id?: string | null;
  pigCount?: number | string | null;
  liveWeights?: string | null;
  hangingWeight?: number | string | null;
  subAttributions?: PigSubAttribution[] | null;
  // Breeding animals on this trip. Deliberately SEPARATE from pigCount /
  // liveWeights / subAttributions, which stay the feeder-batch facts the
  // weigh-ins derive: see tripAttachedBreeders below.
  attachedBreeders?: PigTripAttachedBreeder[] | null;
  [field: string]: unknown;
};

export type PigSubBatch = {
  id?: string | null;
  name?: string | null;
  status?: string | null;
  giltCount?: number | string | null;
  boarCount?: number | string | null;
  originalPigCount?: number | string | null;
  legacyFeedLbs?: number | string | null;
  [field: string]: unknown;
};

export type PigMortalityRow = {
  id?: string | null;
  date?: string | null;
  sub_batch_id?: string | null;
  sub_batch_name?: string | null;
  count?: number | string | null;
  comment?: string | null;
  team_member?: string | null;
  created_at?: string | null;
};

export type PigFeederGroup = {
  id?: string | null;
  batchName?: string | null;
  status?: string | null;
  farmBorn?: boolean | null;
  giltCount?: number | string | null;
  boarCount?: number | string | null;
  originalPigCount?: number | string | null;
  legacyFeedLbs?: number | string | null;
  feedAllocatedToTransfers?: number | string | null;
  // The breeding cycle this batch was generated from, and the fallback birth
  // date for legacy batches with no cycle. Read by feedPlanner's burn walk.
  cycleId?: string | null;
  startDate?: string | null;
  subBatches?: PigSubBatch[] | null;
  processingTrips?: PigProcessingTrip[] | null;
  // Element type owned by src/lib/pigForecast.ts (see the import above).
  plannedProcessingTrips?: readonly StoredPlannedTrip[] | null;
  pigMortalities?: PigMortalityRow[] | null;
  [field: string]: unknown;
};

// What a weigh-in-backed trip's source summary carries. `weights` and the two
// count maps stay `unknown`-valued: they are assembled by the caller from
// weigh_ins rows and every read below coerces.
export type TripSourceSummary = {
  weights?: unknown;
  counts?: Record<string, unknown> | null;
  countsByKey?: Record<string, unknown> | null;
  // The summary is assembled by the CALLER (src/pig/usePigProcessingTrips.ts)
  // and carries display fields this module never reads (count, totalLive,
  // avgLive). The index signature records that rather than pretending the three
  // fields above are the whole object.
  [field: string]: unknown;
};

export type TripSourceSummaryFn = (tripId: string | null | undefined) => TripSourceSummary | null | undefined;

// The trip-source option bag every yield helper forwards. A bare FUNCTION is
// accepted as well as the object form; that is not a convenience, it is the
// shape sourceSummaryForTrip has always branched on.
export type TripSourceOptionsObject = {
  tripSourceSummary?: TripSourceSummaryFn | null;
  sourceSummary?: TripSourceSummary | null;
  subName?: string | null;
};
export type TripSourceOptions = TripSourceSummaryFn | TripSourceOptionsObject | null | undefined;

export type PigBreedingCycle = {
  id?: string | null;
  group?: string | number | null;
  exposureStart?: string | null;
  status?: string | null;
  customSuffix?: string | null;
  sowCount?: number | string | null;
  boar1Tags?: string | null;
  boar2Tags?: string | null;
  boar1Name?: string | null;
  boar2Name?: string | null;
  [field: string]: unknown;
};

// A cycle that survived the id + exposureStart filter, so both are known.
type DatedPigBreedingCycle = PigBreedingCycle & {id: string; exposureStart: string};

export type PigFarrowingRecord = {
  group?: string | number | null;
  farrowingDate?: string | null;
  sow?: string | null;
  // The litter counts feedPlanner's piglet-feed walk reads off the same record.
  alive?: number | string | null;
  totalBorn?: number | string | null;
  deaths?: number | string | null;
  [field: string]: unknown;
};

// One pig_dailys row, as the FCR helper reads it.
export type PigDailyRow = {feed_lbs?: number | string | null; [column: string]: unknown};

export type PigDailysForName =
  | ((name: string | null | undefined) => PigDailyRow[] | null | undefined)
  | null
  | undefined;

export type PigBreedingTimeline = {
  boarStart: string;
  boarEnd: string;
  paddockStart: string;
  paddockEnd: string;
  farrowingStart: string;
  farrowingEnd: string;
  weaningStart: string;
  weaningEnd: string;
  growStart: string;
  growEnd: string;
};

export type PigTransferTotals = {count: number; feedAllocLbs: number; gilts: number; boars: number};

export type PigAgeRange = {
  text: string;
  hasActual: boolean;
  count: number;
  total: number;
  minDays: number | null;
  maxDays: number | null;
};

// parseInt/parseFloat declare a `string` parameter, but these helpers are fed
// raw planner values (numbers, nulls, undefined) and rely on the runtime
// coercion those functions already perform. The cast keeps each call exactly as
// it was rather than introducing String(), which would change behaviour for
// objects and arrays. Same convention as src/lib/cattleNutrition.ts.

// ── PIG BREEDING CONSTANTS ─────────────────────────────────────────────────
export const BOAR_EXPOSURE_DAYS = 45;
export const GESTATION_DAYS = 116; // days from first exposure to first possible farrowing
export const WEANING_DAYS = 42; // 6 weeks
export const GROW_OUT_DAYS = 183; // 6 months

export const PIG_GROUPS = ['1', '2', '3'];
export const BREEDING_STATUSES = ['planned', 'active', 'completed'];

// The two boar names a farm with no ppp-boars-v1 row renders. It lived in
// PigContext as a module-private `DEFAULT_BOAR_NAMES` while that provider was
// the only thing that could produce it; `pig.breeding.core` needs the SAME
// fallback because the boot's hydration is conditional
// (`if (store['ppp-boars-v1'])`) and an absent key has always meant these two
// names rather than blank labels. One definition, imported by both, so the
// route resource and the provider cannot answer differently for an empty farm.
export const DEFAULT_BOAR_NAMES = Object.freeze({boar1: 'MACHINE', boar2: 'AO'});

// The breed and origin dropdown seeds for the breeding-pig registry. Same story
// as DEFAULT_BOAR_NAMES: PigContext held them as module-private useState
// initializers while it was the only producer, and App's boot hydration is
// conditional (`if (store['ppp-breed-options-v1'])`), so an absent key has
// always meant these lists rather than an empty dropdown.
//
// NOTE FOR ANYONE TIDYING app_store: the two keys these back are absent from
// production and look dead. They are NOT. /pig/sows writes both — through
// src/lib/appStoreBlobApi.ts now, where App's persistBreedOptions /
// persistOriginOptions props used to carry them — the first time an operator
// adds a custom breed or origin, and
// tests/static/dead_hydration_removed_static.test.js pins their live hydration
// for exactly that reason.
export const DEFAULT_BREED_OPTIONS = Object.freeze(['Berkshire', 'Duroc', 'Berkshire Cross', 'Duroc/Berkshire Cross']);
export const DEFAULT_ORIGIN_OPTIONS = Object.freeze(['Born on Farm', 'Corey Davis']);

// Colors per group: one base shade for the whole cycle, with a lighter
// shade for Gilts grow-out and a darker shade for Boars grow-out. All
// three groups stay inside the pig program's blue family (no purple per
// project rules) — Group 1 is sky, Group 2 is the core pig blue, and
// Group 3 is slate. Mirrors the broiler per-batch single-color treatment.
export const PIG_GROUP_COLORS = {
  1: {
    boar: '#0EA5E9',
    paddock: '#0EA5E9',
    farrowing: '#0EA5E9',
    weaning: '#0EA5E9',
    gilt: '#7DD3FC',
    boarGrow: '#075985',
  },
  2: {
    boar: '#2563EB',
    paddock: '#2563EB',
    farrowing: '#2563EB',
    weaning: '#2563EB',
    gilt: '#93C5FD',
    boarGrow: '#1E3A8A',
  },
  3: {
    boar: '#475569',
    paddock: '#475569',
    farrowing: '#475569',
    weaning: '#475569',
    gilt: '#94A3B8',
    boarGrow: '#1E293B',
  },
};
export const PIG_GROUP_TEXT = {1: '#E0F2FE', 2: '#DBEAFE', 3: '#F1F5F9'};

// getReadableText now lives in lib/styles.js so every program can use it.
// Re-export here for back-compat with existing pig view imports.
export {getReadableText} from './styles.js';

export const PHASE_LABELS = ['Boar Exposure', 'Exp. Paddock', 'Farrowing', 'Weaning', 'Gilt Grow-out', 'Male Grow-out'];

export function calcBreedingTimeline(exposureStart: string | null | undefined): PigBreedingTimeline | null {
  if (!exposureStart) return null;
  const d0 = new Date(exposureStart + 'T12:00:00');
  const boarEnd = toISO(addDays(d0, BOAR_EXPOSURE_DAYS - 1));
  // Paddock starts day AFTER last boar exposure day, ends day before first possible farrowing
  const paddockStart = toISO(addDays(d0, BOAR_EXPOSURE_DAYS));
  const paddockEnd = toISO(addDays(d0, GESTATION_DAYS - 1));
  const farrowingStart = toISO(addDays(d0, GESTATION_DAYS));
  const farrowingEnd = toISO(addDays(d0, BOAR_EXPOSURE_DAYS - 1 + GESTATION_DAYS));
  const weaningStart = toISO(addDays(d0, BOAR_EXPOSURE_DAYS + GESTATION_DAYS));
  const weaningEnd = toISO(addDays(d0, BOAR_EXPOSURE_DAYS + GESTATION_DAYS + WEANING_DAYS - 1));
  const growStart = toISO(addDays(d0, BOAR_EXPOSURE_DAYS + GESTATION_DAYS + WEANING_DAYS));
  const growEnd = toISO(addDays(d0, BOAR_EXPOSURE_DAYS + GESTATION_DAYS + WEANING_DAYS + GROW_OUT_DAYS - 1));
  return {
    boarStart: exposureStart,
    boarEnd,
    paddockStart,
    paddockEnd,
    farrowingStart,
    farrowingEnd,
    weaningStart,
    weaningEnd,
    growStart,
    growEnd,
  };
}

// ── BREEDING CYCLE LABELS ────────────────────────────────────────────────
// Auto-generate per-year global sequence number for breeding cycles.
// Format: "Group N - YY-NN" (e.g. "Group 1 - 25-01").
// NN resets each year; first cycle to start in any year gets 01, then 02, etc.
// regardless of which group it's in.
export function buildCycleSeqMap(cycles: PigBreedingCycle[] | null | undefined): Record<string, string> {
  const seqMap: Record<string, string> = {};
  // The predicate states exactly what the filter already checked, so the two
  // fields read below need no cast.
  const dated = (cycles || []).filter((c): c is DatedPigBreedingCycle => Boolean(c && c.id && c.exposureStart));
  const sorted = [...dated].sort((a, b) => {
    if (a.exposureStart !== b.exposureStart) return a.exposureStart.localeCompare(b.exposureStart);
    return String(a.id).localeCompare(String(b.id));
  });
  const yearCounts: Record<string, number> = {};
  sorted.forEach((c) => {
    const yr = c.exposureStart.slice(2, 4); // '2025' -> '25'
    yearCounts[yr] = (yearCounts[yr] || 0) + 1;
    seqMap[c.id] = yr + '-' + String(yearCounts[yr]).padStart(2, '0');
  });
  return seqMap;
}
export function cycleLabel(cycle: PigBreedingCycle | null | undefined, seqMap?: Record<string, string> | null): string {
  if (!cycle) return '';
  // customSuffix (when set by admin) overrides the auto year-sequence code.
  // String() on the key reproduces exactly what a JavaScript property access
  // already did for a null/undefined cycle id.
  const autoSuffix = seqMap && seqMap[String(cycle.id)];
  const suffix = (cycle.customSuffix && String(cycle.customSuffix).trim()) || autoSuffix;
  return 'Group ' + cycle.group + (suffix ? ' - ' + suffix : '');
}

export function calcCycleStatus(cycle: PigBreedingCycle): string {
  if (!cycle.exposureStart) return cycle.status || 'planned';
  const today = todayISO();
  const tl = calcBreedingTimeline(cycle.exposureStart);
  if (!tl) return 'planned';
  if (today < cycle.exposureStart) return 'planned';
  if (today > tl.growEnd) return 'completed';
  return 'active';
}

// ── PIG BATCH LEDGER HELPERS ────────────────────────────────────────────────
// Sub-batches are partitions of their parent feeder group. "Started counts"
// (giltCount/boarCount/originalPigCount on both parent and sub) are
// authoritative — they record what entered the batch. Transfers, processing
// trips, and mortality are events recorded in audit logs (breeders[],
// processingTrips, pigMortalities). "Current" is derived ledger-style from
// started − Σ events.

export function pigSlug(s: unknown): string {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function pigSourceCountKeys(s: unknown): string[] {
  const trimmed = String(s || '').trim();
  if (!trimmed) return [];
  const lower = trimmed.toLowerCase();
  const slug = pigSlug(trimmed);
  return lower === slug ? [lower] : [lower, slug];
}

// Resolve a session.batch_id (slug or exact name) to a sub-batch id on the
// parent feeder group. Returns null if no match.
export function resolveSubByBatchId(
  parentGroup: PigFeederGroup | null | undefined,
  batchId: string | null | undefined,
): string | null | undefined {
  if (!batchId || !parentGroup || !Array.isArray(parentGroup.subBatches)) return null;
  const s = pigSlug(batchId);
  for (const sb of parentGroup.subBatches) {
    if (pigSlug(sb.name) === s) return sb.id;
  }
  return null;
}

// Active pig feeder daily-report targets. Each ACTIVE feeder group contributes
// its ACTIVE sub-batches with ledger-current pigs only — there is no parent-batch
// fallback. A parent feeder group with zero sub-batches (or whose sub-batches
// are all processed/inactive/empty) contributes no feeder daily target: feeder
// daily reports exist only while an active sub-batch still has pigs on farm.
// SOWS/BOARS breeding stock are separate non-feeder daily targets handled by
// the caller.
//
// Returns [{id, name, parentBatchName}] in feeder-group order, then sub order,
// so callers (the webform mirror payload and Home's missed-report loop) share
// one derivation and cannot drift.
export function activePigFeederDailyTargets(
  feederGroups: PigFeederGroup[] | null | undefined,
  {
    breeders = [],
    tripSourceSummary = null,
  }: {breeders?: PigBreederRow[] | null; tripSourceSummary?: TripSourceSummaryFn | null} = {},
): {id: string | null | undefined; name: string | null | undefined; parentBatchName: string | null | undefined}[] {
  const out: {
    id: string | null | undefined;
    name: string | null | undefined;
    parentBatchName: string | null | undefined;
  }[] = [];
  for (const g of feederGroups || []) {
    if (!g || g.status !== 'active') continue;
    for (const s of g.subBatches || []) {
      if (!s || s.status !== 'active') continue;
      if (computeSubCurrentCount(g, s, breeders, {tripSourceSummary}) <= 0) continue;
      out.push({id: s.id, name: s.name, parentBatchName: g.batchName});
    }
  }
  return out;
}

// Sum breeders[] transferred from a specific sub of a specific parent batch.
// Returns {count, feedAllocLbs, gilts, boars}.
export function pigTransfersForSub(
  breeders: readonly PigBreederRow[] | null | undefined,
  parentBatchName: string | null | undefined,
  subBatchName: string | null | undefined,
): PigTransferTotals {
  const out: PigTransferTotals = {count: 0, feedAllocLbs: 0, gilts: 0, boars: 0};
  if (!Array.isArray(breeders)) return out;
  for (const b of breeders) {
    if (!b || !b.transferredFromBatch) continue;
    if (b.transferredFromBatch.batchName !== parentBatchName) continue;
    if (b.transferredFromBatch.subBatchName !== subBatchName) continue;
    out.count++;
    out.feedAllocLbs += parseFloat(b.transferredFromBatch.feedAllocationLbs as string) || 0;
    if (b.sex === 'Boar') out.boars++;
    else out.gilts++;
  }
  return out;
}

// Aggregated to the parent (any sub).
export function pigTransfersForBatch(
  breeders: readonly PigBreederRow[] | null | undefined,
  parentBatchName: string | null | undefined,
): PigTransferTotals {
  const out: PigTransferTotals = {count: 0, feedAllocLbs: 0, gilts: 0, boars: 0};
  if (!Array.isArray(breeders)) return out;
  for (const b of breeders) {
    if (!b || !b.transferredFromBatch) continue;
    if (b.transferredFromBatch.batchName !== parentBatchName) continue;
    out.count++;
    out.feedAllocLbs += parseFloat(b.transferredFromBatch.feedAllocationLbs as string) || 0;
    if (b.sex === 'Boar') out.boars++;
    else out.gilts++;
  }
  return out;
}

// Sum trip pigs attributed to a specific sub via trip.subAttributions.
export function pigTripPigsForSub(
  trips: readonly PigProcessingTrip[] | null | undefined,
  subId: string | null | undefined,
  options: TripSourceOptions = {},
): number {
  if (!Array.isArray(trips)) return 0;
  let n = 0;
  for (const t of trips) {
    const sourceCount = processingTripSourceCountForSub(t, subId, options);
    if (sourceCount != null) {
      n += sourceCount;
      continue;
    }
    const atts = t && Array.isArray(t.subAttributions) ? t.subAttributions : [];
    for (const a of atts) if (a && a.subId === subId) n += parseInt(a.count as string) || 0;
  }
  return n;
}

// Total trip pigs across all subAttributions (sum may be < trip.pigCount
// when legacy trips have no attribution; the unattributed remainder is the
// difference).
export function pigTripPigsAttributed(trips: readonly PigProcessingTrip[] | null | undefined): number {
  if (!Array.isArray(trips)) return 0;
  let n = 0;
  for (const t of trips) {
    const atts = t && Array.isArray(t.subAttributions) ? t.subAttributions : [];
    for (const a of atts) n += parseInt(a.count as string) || 0;
  }
  return n;
}

// ── TRIP YIELD HELPERS (extracted from PigBatchesView) ──────────────────────
// Pure parsers/derivations over a processing trip's weight fields. Live weights
// are entered as a free-form string (space/comma separated); parseLiveWeights
// is the single source for that parse so PigBatchesView and computePigBatchFCR
// below can't drift. tripTotalLive sums them; tripYield returns carcass yield %
// (hanging ÷ total live) to one decimal, or null when either side is missing.
export function parseLiveWeights(str: string | null | undefined): number[] {
  return (str || '')
    .split(/[\s,]+/)
    .map((v) => parseFloat(v))
    .filter((v) => !isNaN(v) && v > 0);
}

function sourceSummaryForTrip(
  trip: PigProcessingTrip | null | undefined,
  options: TripSourceOptions = {},
): TripSourceSummary | null | undefined {
  if (!options) return null;
  if (typeof options === 'function') return options(trip && trip.id);
  if (typeof options.tripSourceSummary === 'function') return options.tripSourceSummary(trip && trip.id);
  if (options.sourceSummary) return options.sourceSummary;
  return null;
}

function sourceWeightsFromSummary(summary: TripSourceSummary | null | undefined): number[] {
  // Read into a local: Array.isArray narrows a REFERENCE, and
  // `summary && summary.weights` is an expression. Same value, same result.
  const weights = summary && summary.weights;
  return Array.isArray(weights) ? weights.map((w: unknown) => parseFloat(w as string) || 0).filter((w) => w > 0) : [];
}

function sourceCountForSubName(
  counts: Record<string, unknown> | null | undefined,
  subName: string | null | undefined,
): number | null {
  if (!counts || !subName || typeof counts !== 'object') return null;
  const subKeys = new Set(pigSourceCountKeys(subName));
  if (subKeys.size === 0) return null;
  for (const [key, count] of Object.entries(counts)) {
    const countKeys = pigSourceCountKeys(key);
    if (countKeys.some((k) => subKeys.has(k))) return parseInt(count as string) || 0;
  }
  return null;
}

function processingTripSourceCountForSub(
  trip: PigProcessingTrip | null | undefined,
  subId: string | null | undefined,
  options: TripSourceOptions = {},
): number | null {
  if (!subId) return null;
  const summary = sourceSummaryForTrip(trip, options);
  const sourceWeights = sourceWeightsFromSummary(summary);
  if (sourceWeights.length === 0) return null;
  // A FUNCTION options bag carries no subName: `fn.subName` was already
  // undefined, so the callable branch contributes nothing here either.
  const subName = typeof options === 'function' ? undefined : options && options.subName;
  const keyedCount = sourceCountForSubName(summary && summary.countsByKey, subName);
  if (keyedCount != null) return keyedCount;
  return sourceCountForSubName(summary && summary.counts, subName);
}

export function processingTripWithResolvedWeights(
  trip: PigProcessingTrip | null | undefined,
  options: TripSourceOptions = {},
) {
  const sourceWeights = sourceWeightsFromSummary(sourceSummaryForTrip(trip, options));
  if (sourceWeights.length > 0) {
    return {...(trip || {}), pigCount: sourceWeights.length, liveWeights: sourceWeights.join(' ')};
  }
  return {
    ...(trip || {}),
    pigCount: parseInt((trip && trip.pigCount) as string) || 0,
    liveWeights: (trip && trip.liveWeights) || '',
  };
}

export function processingTripPigCount(
  trip: PigProcessingTrip | null | undefined,
  options: TripSourceOptions = {},
): number {
  // processingTripWithResolvedWeights always resolves pigCount to a number,
  // and parseInt already performed this ToString itself.
  return parseInt(String(processingTripWithResolvedWeights(trip, options).pigCount)) || 0;
}

export function tripTotalLive(trip: PigProcessingTrip | null | undefined, options: TripSourceOptions = {}): number {
  return parseLiveWeights(processingTripWithResolvedWeights(trip, options).liveWeights).reduce((a, b) => a + b, 0);
}
export function tripYield(trip: PigProcessingTrip | null | undefined, options: TripSourceOptions = {}): number | null {
  const live = tripTotalLive(trip, options);
  const hang = parseFloat((trip && trip.hangingWeight) as string) || 0;
  if (!live || !hang) return null;
  return Math.round((hang / live) * 1000) / 10;
}

// ── ATTACHED BREEDING PIGS ───────────────────────────────────────────────────
// A trip can carry a breeding animal (sow / gilt / boar) alongside the feeder
// pigs the weigh-ins produced. The association is written only by the
// migration-210 RPCs and read here.
//
// The accounting rule these helpers exist to keep: a breeder is a trip ANIMAL
// and never a feeder-batch pig. pigCount, liveWeights and subAttributions stay
// weigh-in derived, so every inventory number that reads them — the per-sub
// ledger, the batch current count and the FCR denominator — is unaffected by an
// attachment. Only the ANIMAL total and the Processing count include breeders.

// The same strict test the server applies before casting a planner weight
// (the regex migration 188 introduced and migration 210 reuses). parseFloat
// would read "120junk" as 120 and "1,200" as 1, so a Podio-imported value could
// make the browser believe a weight is known while the server reports it
// unknown. Rejecting the same strings on both sides is what makes "client and
// server cannot disagree" actually true rather than merely intended.
function strictWeight(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) && v > 0 ? v : null;
  if (typeof v !== 'string') return null;
  // SPACES only, because PostgreSQL btrim(x) with no second argument strips
  // spaces and not tabs, newlines or NBSP. Using .trim() here would have made
  // a Podio-imported "\t420" known in the browser and unknown on the server.
  const s = v.replace(/^ +| +$/g, '');
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function tripAttachedBreeders(trip: PigProcessingTrip | null | undefined): PigTripAttachedBreeder[] {
  const raw = trip && trip.attachedBreeders;
  return Array.isArray(raw) ? raw.filter((b) => b && typeof b === 'object') : [];
}

export function tripAttachedBreederCount(trip: PigProcessingTrip | null | undefined): number {
  return tripAttachedBreeders(trip).length;
}

// Total ANIMALS on a trip: feeder-batch pigs (resolved from the weigh-ins, as
// everywhere else) plus attached breeders.
export function tripTotalAnimals(trip: PigProcessingTrip | null | undefined, options: TripSourceOptions = {}): number {
  return processingTripPigCount(trip, options) + tripAttachedBreederCount(trip);
}

// The one place a breeder's live weight is read: the latest positive weigh-in
// on the breeder record, else its lastWeight. Returns null for "unknown" —
// never 0, which would read as a real weight. The attachment itself carries no
// live weight, so there is no third fallback. `_pig_trip_breeder_weights_known`
// in migration 210 reads these same two sources in this same order, so the
// browser and the server can never disagree about whether a weight is known.
export function breederLiveWeight(breeder: PigBreederRow | null | undefined): number | null {
  if (!breeder) return null;
  const weighins = Array.isArray(breeder.weighins) ? (breeder.weighins as {weight?: unknown}[]) : [];
  for (let i = weighins.length - 1; i >= 0; i -= 1) {
    const w = strictWeight(weighins[i] && weighins[i].weight);
    if (w != null) return w;
  }
  return strictWeight(breeder.lastWeight);
}

// An attachment joined to the live breeder row. `breeder` is null when the
// record is gone; the snapshot tag/sex still label the row.
export type ResolvedTripBreeder = {
  breederId: string;
  tag: string;
  sex: string;
  liveWeight: number | null;
  // The animal's own carcass weight from the receipt, NOT part of the trip's
  // feeder-batch hangingWeight.
  hangingWeight: number | null;
  breeder: PigBreederRow | null;
  attachedAt: string | null;
};

export function resolveTripBreeders(
  trip: PigProcessingTrip | null | undefined,
  breeders: readonly PigBreederRow[] | null | undefined,
): ResolvedTripBreeder[] {
  const rows = Array.isArray(breeders) ? breeders : [];
  return tripAttachedBreeders(trip).map((a) => {
    const id = String(a.breederId ?? '');
    const live = rows.find((b) => String((b as {id?: unknown}).id ?? '') === id) || null;
    return {
      breederId: id,
      tag: String((live && (live as {tag?: unknown}).tag) ?? a.tag ?? id),
      sex: String((live && live.sex) ?? a.sex ?? ''),
      liveWeight: breederLiveWeight(live),
      hangingWeight: breederHangingWeight(a),
      breeder: live,
      attachedAt: a.attachedAt ? String(a.attachedAt) : null,
    };
  });
}

// One attached breeder's own carcass weight, as the processor's receipt gives
// it. Null when it was never recorded.
export function breederHangingWeight(attachment: PigTripAttachedBreeder | null | undefined): number | null {
  return strictWeight(attachment && attachment.hangingWeight);
}

// ── THE TWO HANGING WEIGHTS ──────────────────────────────────────────────────
// trip.hangingWeight is the FEEDER-BATCH carcass total. It is matched by the
// trip's liveWeights, so the BATCH yield below is a complete measurement and is
// always presentable. An attached breeder's carcass is NOT in it; hers lives on
// the attachment.
//
// This was established from the real receipt for P-26-02 Trip 4: five batch
// gilts at 1435 lb live / 952 lb hanging (66.3%) plus breeding gilt #32 at
// 376 lb hanging. Had 952 included her, the five pigs would imply a 40.1%
// yield, which is not physically possible — so the two figures are separate.
//
// Carcass yield always uses only the feeder-batch live and hanging weights.
// Attached breeding pigs contribute to head count and separately recorded
// weights, never to this percentage, even when both their weights are known.

export function tripBreederHangingTotal(trip: PigProcessingTrip | null | undefined): number {
  return tripAttachedBreeders(trip).reduce((n, a) => n + (breederHangingWeight(a) || 0), 0);
}

// Every carcass on the trip: the feeder batch plus each attached breeder.
export function tripTotalHanging(trip: PigProcessingTrip | null | undefined): number {
  return (parseFloat((trip && trip.hangingWeight) as string) || 0) + tripBreederHangingTotal(trip);
}

// Where a breeding pig is attached, if anywhere: the reverse of the trip-side
// association, derived rather than stored so the two can never disagree.
export type BreederTripAttachment = {
  groupId: string;
  batchName: string;
  tripId: string;
  tripDate: string | null;
  phase: 'actual' | 'planned';
};

export function findBreederTripAttachment(
  groups: readonly PigFeederGroup[] | null | undefined,
  breederId: string | null | undefined,
): BreederTripAttachment | null {
  const id = String(breederId ?? '');
  if (!id) return null;
  for (const g of Array.isArray(groups) ? groups : []) {
    const phases: [PigProcessingTrip[], 'actual' | 'planned'][] = [
      [(g.processingTrips || []) as PigProcessingTrip[], 'actual'],
      [(g.plannedProcessingTrips || []) as unknown as PigProcessingTrip[], 'planned'],
    ];
    for (const [trips, phase] of phases) {
      for (const t of trips) {
        if (tripAttachedBreeders(t).some((a) => String(a.breederId ?? '') === id)) {
          return {
            groupId: String(g.id ?? ''),
            batchName: String(g.batchName ?? g.id ?? ''),
            tripId: String(t.id ?? ''),
            tripDate: t.date ? String(t.date) : null,
            phase,
          };
        }
      }
    }
  }
  return null;
}

// FCR (feed conversion ratio) for a pig feeder group: adjusted feed
// (raw − transfer credits) ÷ total live weight produced by completed trips.
// Returns null when no trips have happened yet — caller falls back to the
// industry default (3.5).
//
// Same accounting frame as the post-overhaul lbs-per-pig math: numerator
// excludes feed credited to transferred-to-breeding pigs (their feed left
// with them), denominator excludes those pigs (they didn't go to processor).
//
// `dailysForName` is a closure-captured helper from PigBatchesView that
// matches a sub-batch / parent name against pig_dailys rows. Lifted as an
// arg so this helper stays React-free.
export function computePigBatchFCR(
  group: PigFeederGroup | null | undefined,
  dailysForName: PigDailysForName,
  breeders: readonly PigBreederRow[] | null | undefined,
  options: TripSourceOptions = {},
): number | null {
  if (!group) return null;
  const trips = group.processingTrips || [];
  let totalLive = 0;
  for (const t of trips) {
    totalLive += tripTotalLive(t, options);
  }
  if (!(totalLive > 0)) return null;
  const subs = group.subBatches || [];
  let rawFeed = parseFloat(group.legacyFeedLbs as string) || 0;
  if (subs.length > 0) {
    for (const sb of subs) {
      rawFeed += parseFloat(sb.legacyFeedLbs as string) || 0;
      const rows = typeof dailysForName === 'function' ? dailysForName(sb.name) || [] : [];
      for (const d of rows) rawFeed += parseFloat(d.feed_lbs as string) || 0;
    }
  } else {
    const rows = typeof dailysForName === 'function' ? dailysForName(group.batchName) || [] : [];
    for (const d of rows) rawFeed += parseFloat(d.feed_lbs as string) || 0;
  }
  // Transfer credit: prefer sum-of-subs from the breeders[] audit log
  // (canonical, per-sub split). Fall back to parent.feedAllocatedToTransfers
  // for parent-only batches that never had subs.
  const agg = pigTransfersForBatch(breeders, group.batchName);
  const credit = agg.feedAllocLbs > 0 ? agg.feedAllocLbs : parseFloat(group.feedAllocatedToTransfers as string) || 0;
  const adjFeed = Math.max(0, rawFeed - credit);
  if (!(adjFeed > 0)) return null;
  return Math.round((adjFeed / totalLive) * 1000) / 1000;
}

export function pigMortalityForSub(
  group: PigFeederGroup | null | undefined,
  subName: string | null | undefined,
): number {
  let n = 0;
  for (const m of (group && group.pigMortalities) || []) {
    if (m && m.sub_batch_name === subName) n += parseInt(m.count as string) || 0;
  }
  return n;
}
export function pigMortalityForBatch(group: PigFeederGroup | null | undefined): number {
  let n = 0;
  for (const m of (group && group.pigMortalities) || []) n += parseInt(m.count as string) || 0;
  return n;
}

// ── CURRENT-COUNT LEDGER HELPERS (extracted from PigBatchesView) ─────────────
// Current count is ledger-derived (never a persisted currentCount): started
// minus the audit-log events. These three helpers are the single source so the
// pig hub tiles and the future /pig/batches/<id> record page can't drift.

// Per-sub ledger current: started − trip pigs − transfers − mortality, clamped
// to >= 0. This is the raw ledger remainder BEFORE the processed-status
// override (callers that need the discrepancy check read this directly).
export function computeSubLedgerCurrent(
  group: PigFeederGroup | null | undefined,
  sub: PigSubBatch,
  breeders: readonly PigBreederRow[] | null | undefined,
  options: TripSourceOptions = {},
): number {
  const started = (parseInt(sub.giltCount as string) || 0) + (parseInt(sub.boarCount as string) || 0);
  const tripPigs = pigTripPigsForSub((group && group.processingTrips) || [], sub.id, {
    // Spreading a FUNCTION options bag yields {} in JavaScript (a function's own
    // properties are non-enumerable), so the callable branch contributes nothing
    // — spelled out because TypeScript cannot spread a callable union member.
    ...(typeof options === 'function' ? {} : options),
    subName: sub && sub.name,
  });
  const transfers = pigTransfersForSub(breeders, group && group.batchName, sub.name);
  const mortality = pigMortalityForSub(group, sub.name);
  return Math.max(0, started - tripPigs - transfers.count - mortality);
}

// Per-sub current count for display: 0 when the sub is processed, otherwise the
// ledger current.
export function computeSubCurrentCount(
  group: PigFeederGroup | null | undefined,
  sub: PigSubBatch,
  breeders: readonly PigBreederRow[] | null | undefined,
  options: TripSourceOptions = {},
): number {
  return sub.status === 'processed' ? 0 : computeSubLedgerCurrent(group, sub, breeders, options);
}

// Batch (parent feeder group) current count.
//   - With sub-batches: sum of each sub's current count (processed subs = 0).
//   - Parent-only (no subs): parentStarted − parent trip pigs − transfers −
//     mortality, clamped >= 0. When parentStarted is 0 (no stored gilt/boar
//     counts) fall back to the latest daily pig_count the caller passes in, or
//     null. NOTE the parent-only trip count is the raw sum of
//     processingTrips[].pigCount (NOT subAttribution-based) — there is no
//     sub-level attribution to draw from on a parent-only batch; this matches
//     the existing parent-only render path exactly.
// Started count for a parent feeder group. Normally gilt + boar started
// counts; farm-born batches carry no sex split, so they fall back to the
// neutral originalPigCount (set from alive farrowing records at creation).
export function batchStartedCount(group: PigFeederGroup | null | undefined): number {
  const gb =
    (parseInt((group && group.giltCount) as string) || 0) + (parseInt((group && group.boarCount) as string) || 0);
  if (gb > 0) return gb;
  if (group && group.farmBorn) return parseInt(group.originalPigCount as string) || 0;
  return 0;
}

export function computeBatchCurrentCount(
  group: PigFeederGroup | null | undefined,
  breeders: readonly PigBreederRow[] | null | undefined,
  {
    latestDailyPigCount = null,
    tripSourceSummary = null,
  }: {latestDailyPigCount?: number | null; tripSourceSummary?: TripSourceSummaryFn | null} = {},
): number | null {
  const subs = (group && group.subBatches) || [];
  if (subs.length > 0) {
    return subs.reduce((s, sub) => s + computeSubCurrentCount(group, sub, breeders, {tripSourceSummary}), 0);
  }
  const parentTrips = ((group && group.processingTrips) || []).reduce(
    (s, t) => s + processingTripPigCount(t, {tripSourceSummary}),
    0,
  );
  const parentTransfers = pigTransfersForBatch(breeders, group && group.batchName).count;
  const parentMort = pigMortalityForBatch(group);
  const parentStarted = batchStartedCount(group);
  if (parentStarted > 0) {
    return Math.max(0, parentStarted - parentTrips - parentTransfers - parentMort);
  }
  return latestDailyPigCount ?? null;
}

// Reconcile sub-batches against parent. Auto-load repair is intentionally
// NARROW: it only enforces the deterministic invariant
//   sub.originalPigCount === sub.giltCount + sub.boarCount
// for subs whose sum-of-gilts and sum-of-boars already match the parent.
//
// When sub totals don't match the parent (sex-specific: sum giltCount === parent
// giltCount AND sum boarCount === parent boarCount), the function does NOT
// redistribute counts automatically — that's a structural decision an admin
// must make via the parent partition UI. We log a console warning per
// mismatched batch so the inconsistency is visible in dev tools without a
// silent prod rewrite.
//
// Returns {changed, groups, warnings} so callers can surface unresolved
// mismatches to the user.
// ── AGE / TIMELINE HELPERS (extracted from PigBatchesView for reuse) ───────
// Pure helpers — pass breedingCycles / farrowingRecs in rather than reading
// React context. Used by PigBatchesView and the pig-forecast helpers.

// Convert a positive day count into a "Xm Yw" label (months + weeks, days
// dropped per Ronnie's preference on batch tiles). Returns null for ≤ 0 days.
export function daysToMWD(days: number): string | null {
  if (days <= 0) return null;
  const m = Math.floor(days / 30);
  const w = Math.floor((days % 30) / 7);
  return `${m}m ${w}w`;
}

// Farrowing records that belong to a given breeding cycle: must match the
// cycle's group AND fall within the theoretical farrowing window (with a
// 14-day buffer for edge cases).
export function cycleRecords(
  cycle: PigBreedingCycle | null | undefined,
  farrowingRecs: readonly PigFarrowingRecord[] | null | undefined,
): PigFarrowingRecord[] {
  if (!cycle || !Array.isArray(farrowingRecs)) return [];
  const tl = calcBreedingTimeline(cycle.exposureStart);
  if (!tl) return [];
  return farrowingRecs.filter((r) => {
    if (r.group !== cycle.group) return false;
    if (!r.farrowingDate) return false;
    const rd = new Date(r.farrowingDate + 'T12:00:00');
    const wStart = new Date(tl.farrowingStart + 'T12:00:00');
    const wEnd = addDays(tl.farrowingEnd, 14);
    return rd >= wStart && rd <= wEnd;
  });
}

// Age range for the pigs in a breeding cycle as of a reference date.
// Uses actual farrowing dates when present, falls back to the theoretical
// farrowing window (marked "(est.)" in the rendered text). asOfDate may be
// pinned by the caller (e.g. the latest processor-trip date once a batch's
// current count hits 0) so the displayed age stops advancing.
//
// Returns {text, hasActual, count, total, minDays, maxDays}:
//   text       — display string (e.g. "5m 2w – 5m 5w" or "Up to 6m 0w (est.)")
//   hasActual  — true when actual farrowing records were used
//   count      — number of farrowing records found for this cycle
//   total      — cycle.sowCount as integer (0 if missing)
//   minDays    — youngest age in days at ref, clamped to 0 (never negative)
//                or null when the cycle has no usable timeline
//   maxDays    — oldest age in days at ref, or null when same
//
// minDays/maxDays power the planned-trip projector's pre-weigh-in mode
// (recalculateProjections cycleAgeDaysAtRef arg). Both are null when the
// cycle is missing, exposureStart is missing, or the session/ref is before
// any farrowing date (the not-yet-born clamp).
export function calcAgeRange(
  cycleId: string | null | undefined,
  asOfDate: Date | null | undefined,
  breedingCycles: PigBreedingCycle[] | null | undefined,
  farrowingRecs: readonly PigFarrowingRecord[] | null | undefined,
): PigAgeRange {
  const cycles = Array.isArray(breedingCycles) ? breedingCycles : [];
  const cycle = cycles.find((c) => c.id === cycleId);
  if (!cycle) return {text: '—', hasActual: false, count: 0, total: 0, minDays: null, maxDays: null};
  const tl = calcBreedingTimeline(cycle.exposureStart);
  if (!tl) return {text: '—', hasActual: false, count: 0, total: 0, minDays: null, maxDays: null};

  const recs = cycleRecords(cycle, farrowingRecs);
  const ref = asOfDate instanceof Date && !isNaN(asOfDate.getTime()) ? asOfDate : new Date();

  let firstDate: Date;
  let lastDate: Date;
  let hasActual = false;
  if (recs.length > 0) {
    // `a - b` on two Dates coerces through valueOf(), which IS getTime(); the
    // explicit call is the same subtraction spelled so the checker can see it.
    const dates = recs.map((r) => new Date(r.farrowingDate + 'T12:00:00')).sort((a, b) => a.getTime() - b.getTime());
    firstDate = dates[0];
    lastDate = dates[dates.length - 1];
    hasActual = true;
  } else {
    firstDate = new Date(tl.farrowingStart + 'T12:00:00');
    lastDate = new Date(tl.farrowingEnd + 'T12:00:00');
  }

  // Same valueOf()-is-getTime() point as the sort above.
  const oldestDays = Math.round((ref.getTime() - firstDate.getTime()) / 86400000);
  const youngestDays = Math.round((ref.getTime() - lastDate.getTime()) / 86400000);
  const total = parseInt(cycle.sowCount as string) || 0;

  if (oldestDays <= 0) {
    return {text: 'Not yet born', hasActual, count: recs.length, total, minDays: null, maxDays: null};
  }
  const oldest = daysToMWD(oldestDays);
  const text =
    youngestDays <= 0
      ? `Up to ${oldest}${!hasActual ? ' (est.)' : ''}`
      : `${daysToMWD(youngestDays)} – ${oldest}${!hasActual ? ' (est.)' : ''}`;
  // Numeric bounds for the projector. Clamp youngest to 0 so the band
  // doesn't go negative when the youngest pigs haven't quite been born yet.
  return {
    text,
    hasActual,
    count: recs.length,
    total,
    minDays: Math.max(0, youngestDays),
    maxDays: oldestDays,
  };
}

export function reconcileFeederGroupsFromBreeders(feederGroups: PigFeederGroup[] | null | undefined): {
  changed: boolean;
  groups: PigFeederGroup[];
  warnings: string[];
} {
  let changed = false;
  const warnings: string[] = [];
  const groups = (feederGroups || []).map((g) => {
    const subs = g.subBatches || [];
    if (subs.length === 0) return g;
    const sumGilts = subs.reduce((s, sb) => s + (parseInt(sb.giltCount as string) || 0), 0);
    const sumBoars = subs.reduce((s, sb) => s + (parseInt(sb.boarCount as string) || 0), 0);
    const parentGilts = parseInt(g.giltCount as string) || 0;
    const parentBoars = parseInt(g.boarCount as string) || 0;
    if (sumGilts !== parentGilts || sumBoars !== parentBoars) {
      const msg =
        '[reconcile] ' +
        (g.batchName || g.id) +
        " sub totals don't match parent: gilts " +
        sumGilts +
        '/' +
        parentGilts +
        ', boars ' +
        sumBoars +
        '/' +
        parentBoars +
        ' (skipped — admin to resolve via partition UI)';
      warnings.push(msg);
      if (typeof console !== 'undefined' && console.warn) console.warn(msg);
      return g;
    }
    // Sex sums match — only enforce the OPC = gilt+boar invariant. This
    // is a deterministic, lossless rewrite (every sub has a single right
    // answer and we're rewriting only that field).
    let needsOPC = false;
    const newSubs = subs.map((sb) => {
      const opc = (parseInt(sb.giltCount as string) || 0) + (parseInt(sb.boarCount as string) || 0);
      if ((parseInt(sb.originalPigCount as string) || 0) !== opc) {
        needsOPC = true;
        return {...sb, originalPigCount: opc};
      }
      return sb;
    });
    if (needsOPC) {
      changed = true;
      return {...g, subBatches: newSubs};
    }
    return g;
  });
  return {changed, groups, warnings};
}

// ── FARROWING SIRE RESOLUTION ──────────────────────────────────────────────
// Which boar sired a farrowing record: find the breeding cycle whose farrowing
// window contains the record's farrowing date (with a 14-day tail), then match
// the sow's tag against that cycle's boar1/boar2 tag lists. Returns null when
// no cycle matches -- deliberately NOT the record's own stored `sire`, so an
// unmatched record reads as Unknown rather than as a stale guess.
//
// B2.3: moved verbatim out of App() in main.jsx. The two values it used to
// close over -- the breeding cycles and the default boar names -- are now
// explicit parameters, which is the only change.
export function resolveFarrowingSire(
  rec: PigFarrowingRecord | null | undefined,
  breedingCycles: PigBreedingCycle[],
  boarNames: {boar1?: string | null; boar2?: string | null},
): string | null | undefined {
  if (!rec || !rec.sow) return null;
  var sowTag = rec.sow.trim();
  for (var ci = 0; ci < breedingCycles.length; ci++) {
    var c = breedingCycles[ci];
    var tl = calcBreedingTimeline(c.exposureStart);
    if (!tl) continue;
    if (!rec.farrowingDate) continue;
    var rd = new Date(rec.farrowingDate + 'T12:00:00');
    if (rd < new Date(tl.farrowingStart + 'T12:00:00') || rd > addDays(new Date(tl.farrowingEnd + 'T12:00:00'), 14))
      continue;
    var b1Tags = (c.boar1Tags || '')
      .split(/[\n,]+/)
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);
    var b2Tags = (c.boar2Tags || '')
      .split(/[\n,]+/)
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);
    if (b1Tags.includes(sowTag)) return c.boar1Name || boarNames.boar1;
    if (b2Tags.includes(sowTag)) return c.boar2Name || boarNames.boar2;
  }
  // No cycle match — return null (Unknown), not the hardcoded r.sire
  return null;
}
